import Colors from '@utils/Colors.js'

import {radius, shadows, statusColors, zIndex} from '../styles.js'

// The feedback components (Alert, Badge, Toast, Spinner, ProgressBar, ToolTip, Info, Icon) take their look from here.
// Status colours come from statusColors in ../styles.ts. Change a value here instead of in a component. None of them
// has outer margins: the space around them comes from the caller's style or layout (Stack, Row).

export type StatusVariant = 'success' | 'info' | 'warning' | 'error'

// The icon of each status in an Alert and a Toast
export const statusIcons: Record<StatusVariant, string> = {
    success: 'mdi mdi-check-circle-outline',
    info: 'mdi mdi-information-outline',
    warning: 'mdi mdi-alert',
    error: 'mdi mdi-alert'
}

export const alertStyle = {
    minHeight: 36,
    padding: '6px 10px 6px 10px',
    radius: radius.default,
    text: Colors.textDark,
    iconSize: 18,
    // Between the icon, the text and the close button
    iconGap: 12
}

export type BadgeVariant = StatusVariant | 'neutral' | 'grey' | 'white'

export const badgeStyle = {
    height: {sm: 20, md: 24},
    paddingX: 12,
    fontSize: 12,
    radius: radius.pill,
    border: Colors.border,
    text: Colors.textDark,
    textOnDark: Colors.textLight
}

/** Background of a Badge variant; no variant is neutral, 'grey' is the old name of neutral */
export function getBadgeBackground(variant?: string) {
    if (variant === 'white') {
        return Colors.white
    }

    const status = statusColors[variant as StatusVariant]
    return status ? status.light : statusColors.neutral.light
}

export const toastStyle = {
    background: Colors.buttonSolid,
    text: Colors.textLight,
    minWidth: 200,
    // From the bottom of the window
    bottom: 12,
    padding: '10px 20px 12px 12px',
    radius: radius.default,
    shadow: shadows.floating,
    iconSize: 24,
    iconGap: 12,
    zIndex: zIndex.toast,
    // Shown this long unless showToast gets a time
    duration: 2500,
    transition: 200
}

// The icon of a Toast per variant, its colour is the bright status colour
export const toastIcons: Record<StatusVariant, string> = {
    ...statusIcons,
    success: 'mdi mdi-checkbox-marked-circle-outline'
}

export const spinnerStyle = {
    size: 20,
    borderWidth: 2,
    opacity: 0.8,
    color: Colors.textExtraDark,
    // Above content it is placed over with position absolute (a list that loads)
    zIndex: 1001,
    duration: '0.75s'
}

export const progressBarStyle = {
    height: 20,
    radius: radius.default,
    background: Colors.grey20,
    fill: Colors.successBright,
    fontSize: 12
}

export const tooltipStyle = {
    height: 25,
    paddingX: 12,
    fontSize: 12,
    background: Colors.grey60,
    text: Colors.textLight,
    radius: radius.default,
    opacity: 0.98,
    // Between the anchor and the tooltip
    gap: 3,
    zIndex: zIndex.tooltip,
    // Hovering this long shows it, leaving hides it after hideDelay
    showDelay: 800,
    hideDelay: 100,
    fadeIn: 600
}

export const iconSizes = {xs: 12, sm: 14, md: 16, lg: 20, default: 18}

export const iconColors = {
    default: Colors.textDark
}

// Keyframes of the Spinner and the ToolTip fade-in. Inline styles can't define keyframes, so they are injected once
// as a <style> element in the head, like ensureFocusStyles in UI/styles.ts.
export const spinnerAnimation = 'ui-spinner-rotate'
export const tooltipAnimation = 'ui-tooltip-fade-in'

const styleElementId = 'veloyd-feedback-styles'

const css = `
@keyframes ${spinnerAnimation} {
    0% { transform: rotate(0deg); }
    50% { transform: rotate(180deg); }
    100% { transform: rotate(360deg); }
}

@keyframes ${tooltipAnimation} {
    from { opacity: 0; }
    to { opacity: ${tooltipStyle.opacity}; }
}
`

/** Adds the feedback keyframes to the document once (a no-op without a document) */
export function ensureFeedbackStyles() {
    if (typeof document === 'undefined' || document.getElementById(styleElementId)) {
        return
    }

    const style = document.createElement('style')
    style.id = styleElementId
    style.textContent = css
    document.head.appendChild(style)
}
